
import React, { useEffect, useState } from 'react';
import { Property, AppUser, UserRole } from '../../../types';
import { getSimilarPropertiesCodes } from '../../../services/similarPropertiesService';
import { searchByCodesApi } from '../../../services/propertyApiClient';
import { PropertyCard } from '../PropertyCard';
import { SpinnerIcon } from '../../Icons';
import { getResolvedCommissionRate } from '../../../utils/commissionUtils';

interface SimilarPropertiesCarouselProps {
  currentPropertyCode: string;
  appUser: AppUser | null;
  inmobiliariasConfig?: Record<string, any>;
  onViewProperty: (property: Property) => void;
  onShareProperty: (property: Property) => void;
}

export const SimilarPropertiesCarousel: React.FC<SimilarPropertiesCarouselProps> = ({
  currentPropertyCode,
  appUser,
  inmobiliariasConfig,
  onViewProperty,
  onShareProperty
}) => {
  const [similarProperties, setSimilarProperties] = useState<Property[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetchSimilar = async () => {
      if (!currentPropertyCode) return;
      setIsLoading(true);
      try {
        const codes = await getSimilarPropertiesCodes(currentPropertyCode);
        if (codes && codes.length > 0) {
          const response = await searchByCodesApi(codes.slice(0, 10));
          const items = response?.items || [];

          const isAnyBroker = appUser?.role && [
            UserRole.BROKER, 
            UserRole.BROKER_INTERNO, 
            UserRole.BROKER_PREMIUM, 
            UserRole.BROKER_EXTERIOR,
            UserRole.BROKERS_LEADS
          ].includes(appUser.role);

          const filtered = items.filter(property => {
            const isVisible = property.status === 'Activo' || property.status === 'Actualizado' || property.status === 'ActualizadoCodigo';
            if (!isVisible) return false;

            if (isAnyBroker) {
              const isBrokerVisible = property.es_visible_broker === true || (property as any).es_visible_broker === 1;
              if (!isBrokerVisible) return false;
              const rate = getResolvedCommissionRate(property, appUser, inmobiliariasConfig || {});
              if (rate <= 0) return false;
            }
            return true;
          });

          if (isMounted) setSimilarProperties(filtered);
        } else if (isMounted) {
          setSimilarProperties([]);
        }
      } catch (error) {
        console.error("[SimilarPropertiesCarousel] Error loading similar properties:", error);
        if (isMounted) setSimilarProperties([]);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchSimilar();
    return () => { isMounted = false; };
  }, [currentPropertyCode, appUser, inmobiliariasConfig]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 bg-gray-50/50 rounded-3xl border border-dashed border-gray-200">
        <SpinnerIcon className="w-8 h-8 text-sky-600 animate-spin mb-3" />
        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Buscando inmuebles similares...</p>
      </div>
    );
  }

  if (similarProperties.length === 0) return null;

  return (
    <section className="mt-12 animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-black uppercase tracking-tighter text-[#00234F]">Inmuebles Similares</h3>
        <span className="text-[10px] font-black bg-sky-100 text-sky-700 px-2 py-1 rounded-md uppercase">{similarProperties.length} encontrados</span>
      </div>
      <div className="flex overflow-x-auto pb-6 gap-6 custom-scrollbar snap-x -mx-2 px-2 items-stretch">
        {similarProperties.map(property => (
          <div key={property.id} className="min-w-[280px] max-w-[320px] snap-start h-auto">
            <PropertyCard
              property={property}
              isSelected={false}
              onView={() => onViewProperty(property)}
              onShare={() => onShareProperty(property)}
              onToggleSelect={() => {}}
              isCompact={true}
              source="brokers"
              appUser={appUser}
              onInterested={() => {}}
              inmobiliariasConfig={inmobiliariasConfig}
            />
          </div>
        ))}
      </div>
    </section>
  );
};
